<script lang="ts">
	import { beforeNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import { brandsSlugs } from '$lib';
	import { m } from '$lib/paraglide/messages.js';
	import { slide } from 'svelte/transition';

	const { menu }: { menu: Menu[] } = $props();

	let hoveredCategory = $state<Menu>();
	let hoverTimeout: number | undefined;

	function handleMouseEnter(e: PointerEvent, category: Menu) {
		if (e.pointerType === 'touch') return;
		if (hoverTimeout) {
			clearTimeout(hoverTimeout);
			hoverTimeout = undefined;
		}
		hoverTimeout = window.setTimeout(
			() => {
				hoveredCategory = category;
				hoverTimeout = undefined;
			},
			hoveredCategory ? 0 : 150,
		);
	}

	function handleMouseLeave() {
		if (hoverTimeout) {
			clearTimeout(hoverTimeout);
			hoverTimeout = undefined;
		}
		hoveredCategory = undefined;
	}

	const buttonCategories: Menu[] = $derived(
		hoveredCategory?.children?.filter((category: Menu) => category.is_button_menu) ?? [],
	);
	const expandedCategories: Menu[] = $derived(
		hoveredCategory?.children?.filter((category: Menu) => !category.is_button_menu) ?? [],
	);

	const gridColumnCount = 8;
	const gridRowCount = $derived(
		Math.ceil(
			expandedCategories.length / (gridColumnCount - (buttonCategories.length > 0 ? 1 : 0)),
		),
	);

	function getClassForMenuClass(menuClass: string | null): string {
		switch (menuClass) {
			case 'sticky':
				return 'font-bold';

			default:
				return '';
		}
	}

	beforeNavigate(() => {
		hoveredCategory = undefined;
	});
</script>

<div class="hidden w-full xl:block" role="navigation">
	<nav onpointerleave={handleMouseLeave}>
		<div class="flex items-center gap-8 py-2">
			{#each menu as category, i (category.slug)}
				<div
					class="relative border-b-2 border-b-transparent hover:border-b-black {getClassForMenuClass(
						category.menu_class,
					)}"
					onpointerenter={(e) => handleMouseEnter(e, category)}
					role="link"
					tabindex={i}
				>
					<a href="/{category.slug}" class="text-sm">
						{category.name}
					</a>
				</div>
			{/each}

			<div class="relative border-b-2 border-b-transparent hover:border-b-black" role="link">
				<a href="/{brandsSlugs[page.data.store.language]}" class="text-sm">
					{m.brands()}
				</a>
			</div>
		</div>

		{#if hoveredCategory && hoveredCategory.children.length > 0}
			<div class="absolute left-0 z-50 w-full border-b bg-white shadow-lg" transition:slide>
				<!-- eslint-disable custom-rules/no-inline-styles -->
				<div
					class="full-container mx-auto grid max-h-[75vh] gap-5 overflow-y-auto p-8"
					style="grid-template-columns: repeat({gridColumnCount}, minmax(0, 1fr)); grid-template-rows: repeat({gridRowCount}, auto);"
				>
					<!-- eslint-enable custom-rules/no-inline-styles -->
					{#if buttonCategories.length}
						<div class="row-span-full flex flex-col gap-y-2">
							{#each buttonCategories as category (category.slug)}
								<a href="/{category.slug}" class="mb-3 font-bold">
									<div
										class="rounded-md border border-neutral-800 p-1 text-center text-xs font-bold uppercase transition-colors hover:bg-neutral-900 hover:text-white"
									>
										{category.name}
									</div>
								</a>
							{/each}
						</div>
					{/if}

					{#each expandedCategories as category (category.slug)}
						<div class="text-sm">
							<a href="/{category.slug}" class="mb-3 block border-b border-neutral-800 font-bold">
								{category.name}
							</a>
							{#if category.children.length > 0}
								<div class="space-y-1">
									{#each category.children as subCategory (subCategory.slug)}
										<a href="/{subCategory.slug}" class="block text-neutral-600 hover:text-black">
											{subCategory.name}
										</a>
									{/each}
									<a href="/{category.slug}" class="block font-bold">
										{m.view_all_products()}
									</a>
								</div>
							{/if}
						</div>
					{/each}
				</div>
			</div>
		{/if}
	</nav>
</div>
